import { useEffect, useRef, useState } from 'react'

import { showsPicture, type DayCardDisplay } from '../../lib/dayCardDisplay'
import { useTexte } from '../../texte'
import { ensureSettings, useKartenlink, skipQuery, useMusikkarte, useTageskarte, useWetterkarte } from '../../hooks/queries'
import { useQueryClient } from '@tanstack/react-query'
import { versatzDesGeraets } from '../../lib/ortszeit'
import { ApiError, api, fehlertext, netzStand, ohneNetz } from '../../api/client'
import type { DayCardFilm, DayCardPreview, MapStyle, Message } from '../../api/types'
import { insertionPlace, type Insertion } from '../../lib/insertion'
import { holeOrt } from '../../lib/ortung'
import { merken, neuerSchluessel } from '../../lib/outbox'
import { useWartendesWetter } from '../../hooks/useOutbox'
import { sendeOrt, useSendKey } from '../../hooks/useSendKey'
import { zeichneTagesroute } from './tagesroute'
import { beginTerrain } from './terrainPreload'
import { FILM_MAX_ZOOM, buildRouteFilm, stampRouteFilm, dayLine, encodableFormat, filmFormat, filmInput, profileCover, scriptFor, type FilmSignal, type FilmSettings } from './routeFilm'
import { profileOf, tourScript } from '../../lib/tourScript'
import { TOUR_DEFAULTS, type TourChoice } from '../../lib/tourOptions'
import { canDraw3d } from '../../lib/vehicle3dView'
import type { FilmProgress } from './RouteFilmSheet'
import { transferVideo } from '../../lib/video'
import { notiere } from '../../lib/sendeprotokoll'
import { STRICH_VORGABE } from '../map/strich'
import { useBuchquelle } from '../export/buchquelle'

/**
 * What sits behind the `+`: the weather, the weather somewhere else, the day
 * card, the poll, the GIF picker and the music.
 *
 * Pulled out of `Composer.tsx` in 1.23.0. Six entries, and each one has the
 * same shape: a sheet that opens, a question to the server, a line saying
 * which step is running, and a sentence instead of silence when there is no
 * net. That shape is the reason for one place; the sixth (#102, 1.57.0)
 * would otherwise have been the sixth copy.
 *
 * **A hook and not a component**, although the plan said `Plusmenue.tsx`.
 * Measured: `.anhangwahl` and `.gifwahl` are blocks in the flow, siblings of
 * `.composer` inside `.composer-area` -- the `+` itself is a child of
 * `.composer`, and the status line belongs among the other status lines above
 * both. A component holding all three would have to move nodes in the tree;
 * the rule for this rebuild is that code moves and nothing else does. So the
 * code moves and the tree stays.
 */
export function usePlusMenu({
  tripId,
  istEntwurf,
  reiseKarte,
  tagAenderung = null,
  onTagAenderungFertig,
  insertion = null,
}: {
  tripId: number
  /**
   * Before or after a chosen post (#130): every card of this menu goes
   * there, and only with a net -- the basket knows no place. `onDone` gets
   * the new post's id.
   */
  insertion?: (Insertion & { onDrop: () => void; onDone: (messageId: number) => void }) | null
  /** Schreibt diese Zeile Entwürfe? Die Karte geht dann in den Entwurf. */
  istEntwurf: boolean
  /** Die Farben der Reise -- die Tageskarte zeichnet ihre eigene Linie damit. */
  reiseKarte: MapStyle | undefined
  /**
   * A day card to compute anew (1.86.0): the page hands the post over, this
   * hook opens the same sheet for its day, and the choice goes to the same
   * post. Here and not on the page, because the drawing of the map lives
   * here (`zeichneTagesroute`) -- one road, as for a new card.
   */
  tagAenderung?: Message | null
  onTagAenderungFertig?: () => void
}) {
  const t = useTexte()
  /** Where the card goes (#130): the place alone, or null for the end. */
  const stelle = insertionPlace(insertion)
  /** After an inserted card: the marker goes, and the page jumps to it. */
  function eingefuegt(antwort: unknown) {
    if (insertion === null) return
    insertion.onDrop()
    const id = typeof antwort === 'object' && antwort !== null && 'message_id' in antwort
      ? Number((antwort as { message_id: number }).message_id)
      : typeof antwort === 'object' && antwort !== null && 'id' in antwort ? Number((antwort as { id: number }).id) : 0
    if (id > 0) insertion.onDone(id)
  }
  /** Steht das Blatt mit „Foto oder Video / GIF suchen" offen? */
  const [anhangOffen, setAnhangOffen] = useState(false)
  const [gifOffen, setGifOffen] = useState(false)
  const [abstimmungOffen, setAbstimmungOffen] = useState(false)
  /** „Musik hinzufügen" (#102) steht offen. */
  const [musikOffen, setMusikOffen] = useState(false)
  const musik = useMusikkarte(tripId)
  /** "Link zur Karte" (1.113.0) stands open. */
  const [kartenlinkOffen, setKartenlinkOffen] = useState(false)
  const kartenlink = useKartenlink(tripId)
  const wetter = useWetterkarte(tripId)
  /** "Wo und wann?" is open (#83). */
  const [wetterwahlOffen, setWetterwahlOffen] = useState(false)
  const tageskarte = useTageskarte(tripId, istEntwurf)
  const tagSchluessel = useSendKey(sendeOrt(tripId, undefined, istEntwurf))
  // The weather at a chosen place and hour keeps its key while the choice
  // stays the same (1.78.0): a retry after a lost answer is the same card.
  const wetterSchluessel = useSendKey(sendeOrt(tripId, undefined, istEntwurf))
  const buchquelle = useBuchquelle(tripId)
  const queryClient = useQueryClient()
  /** The days to choose from, while the question "Welcher Tag?" is open (0.41.0). */
  const [tagwahl, setTagwahl] = useState<DayCardPreview | null>(null)
  /**
   * The question "Welcher Tag?" before its days are known (1.100.0): the
   * sheet stands at the tap with a running bar (`TagwahlWarten`). Each
   * request gets a number; an answer whose number is no longer the current
   * one was cancelled and is dropped.
   */
  const [tagLaedt, setTagLaedt] = useState(false)
  const tagAnfrage = useRef(0)
  /** The modal sheet of the film (#90): where it stands, while it stands. */
  const [film, setFilm] = useState<FilmProgress | null>(null)
  /** The running film's cancel flag; `null` when none runs. */
  const filmSignal = useRef<FilmSignal | null>(null)
  /** Why it was cancelled: by the button, or by leaving the app. */
  const cancelReason = useRef<'button' | 'left'>('button')
  /**
   * The line this menu reports to the one message line (1.80.0,
   * `useMeldezeile`). `n` counts up, so that the same sentence twice is news
   * again; `ohneNetz` makes it red and lets it go when the net comes back.
   * `endet` marks a sentence about something finished (1.142.0): it goes
   * with the next message (`nachNachricht()`), while a sentence about
   * something still running stays until that road clears it.
   */
  const [aktionStand, setAktionStand] = useState<
    { text: string; art: 'fehler' | 'hinweis'; ohneNetz: boolean; endet: boolean; n: number } | null
  >(null)
  /**
   * Hängt die Zeile an einem Eintrag im Ausgangskorb? Dann räumt der Korb
   * sie weg, nicht der Tipp.
   *
   * **Ein Merker und kein Vergleich mit dem Satz.** Zu prüfen, ob dort
   * gerade `t.chat.wetterWartet` steht, wäre eine zweite Stelle, an der der
   * Wortlaut zählt -- die erste Umformulierung ließe die Zeile wieder
   * stehen.
   */
  const [standWartet, setStandWartet] = useState<{ schluessel: string; gesehen: boolean } | null>(null)

  /**
   * Eine Zeile setzen -- **die eine Stelle**, denn zu ihr gehören zwei
   * Zustände.
   *
   * Jeder Weg hier unten sagt, was gerade läuft, und räumt am Ende wieder
   * auf. Stünde `setStandWartet` daneben in jedem dieser Wege, bliebe er
   * beim nächsten neuen Eintrag stehen.
   */
  function zeigeStand(
    text: string | null,
    wartetImKorb: string | null = null,
    art: 'fehler' | 'hinweis' = 'hinweis',
    wegenNetz = false,
    endet = false,
  ) {
    setAktionStand((alt) => (text === null ? null : { text, art, ohneNetz: wegenNetz, endet, n: (alt?.n ?? 0) + 1 }))
    setStandWartet(wartetImKorb === null ? null : { schluessel: wartetImKorb, gesehen: false })
  }

  /** A failure: red, and when it was the net, gone with the net's return. */
  function zeigeFehler(fehler: unknown) {
    zeigeStand(fehlertext(fehler), null, 'fehler', ohneNetz(fehler), true)
  }

  /** The last word of a road that is over: it goes with the next message. */
  function zeigeErgebnis(text: string | null) {
    zeigeStand(text, null, 'hinweis', false, true)
  }

  /**
   * A message went out (1.142.0, `aufraeumen()` in `Composer`). From use:
   * "sie sollten spätestens bei der nächsten Nachricht weggehen" -- until
   * here "Das 3D-Fahrzeug ließ sich nicht zeichnen" stood under the chat
   * until the next tap in this menu, for days if need be. Only a finished
   * sentence goes; one about weather being fetched or waiting in the basket
   * is still true and clears itself.
   */
  function nachNachricht() {
    setAktionStand((alt) => (alt?.endet === true ? null : alt))
  }

  /**
   * An entry that needs the net, tapped without one (1.80.0): the entry
   * stands greyed in the menu, and the tap says why -- red, like every
   * sentence about the missing net.
   */
  function ohneNetzSagen(text: string) {
    zeigeStand(text, null, 'fehler', true, true)
  }

  /**
   * **Und weg, sobald die Karte da ist** (1.54.2). Aus dem Betrieb, mit
   * Bildschirmfoto: „Wetter ohne Netz geht, aber die Meldung unten bleibt
   * auch nachdem die Wetterdaten geladen sind." Sie sagte „Die Wetterkarte
   * kommt, sobald Netz da ist" -- unter einer Karte, die schon dastand.
   *
   * Der Tipp kann sie nicht wegnehmen: er ist längst vorbei, wenn der
   * Ausgangskorb schickt. Also hängt sie am Korb, und die Tür dorthin ist
   * dieselbe wie die der wartenden Karte (`useWartendesWetter`) -- ist
   * nichts mehr da, das wartet, ist die Zeile falsch. Das gilt auch fürs
   * Verwerfen: auch dann wartet nichts mehr.
   *
   * **Und es hängt am Schlüssel des Eintrags, nicht an der Zahl.** Eine
   * leere Liste heißt zweierlei: „noch nicht nachgesehen" (der Korb liegt
   * in IndexedDB und antwortet einen Augenblick später) und „nichts mehr
   * da". Ohne diese Unterscheidung nahm der Rückbau die Zeile weg, kaum
   * dass sie stand -- gemessen im Prüflauf, eine Runde lang.
   */
  const wartendesWetter = useWartendesWetter(tripId, istEntwurf)
  useEffect(() => {
    if (standWartet === null || wartendesWetter === null) return
    const liegtDrin = wartendesWetter.some((e) => e.clientKey === standWartet.schluessel)
    // Erst sehen, dann räumen: eine leere Liste heißt vor dem ersten Blick
    // „noch nicht nachgesehen", und danach „draußen oder verworfen".
    if (liegtDrin && !standWartet.gesehen) setStandWartet({ ...standWartet, gesehen: true })
    else if (!liegtDrin && standWartet.gesehen) zeigeStand(null)
  }, [standWartet, wartendesWetter])

  /**
   * Get the position, send the weather -- one tap, two steps, and each says
   * what it does.
   *
   * **Without the net into the basket** (#98, 1.54.0; until then a sentence
   * and nothing). GPS works without a net; the tap remembers the place and
   * the moment, the waiting card stands in the chat, and the basket sends
   * both once there is a net -- the server asks Apple for that moment. The
   * same road as a waiting point (`HighlightBogen.inDenKorb`).
   */
  async function wetterSchicken() {
    zeigeStand(t.chat.wetterOrtung)
    try {
      const ort = await holeOrt()
      /*
       * **One key for this tap, online and in the basket** (1.78.0). The
       * online attempt went without one, and a network error on it ended as
       * an error line instead of the basket -- after a lost answer the next
       * tap made a second card. Now the same key goes out first and, when
       * the net fails, into the basket: whichever arrives, it is one card.
       */
      const schluessel = neuerSchluessel()
      const moment = new Date().toISOString()
      // The zone of the device at the tap (1.86.0): the card shows its hour in it.
      const zone = versatzDesGeraets()
      const inDenKorb = async () => {
        await merken({
          art: 'wetter',
          tripId,
          entwurf: istEntwurf,
          bilder: [],
          clientKey: schluessel,
          wetter: { lat: ort.lat, lon: ort.lon, at: moment, tz_offset_min: zone, draft: istEntwurf },
        })
        zeigeStand(t.chat.wetterWartet, schluessel)
      }
      // Inserting needs the net (#130): no basket, a sentence.
      if (!netzStand() && insertion !== null) {
        ohneNetzSagen(t.chat.einfuegen.brauchtNetz)
        return
      }
      if (!netzStand()) {
        await inDenKorb()
        return
      }
      zeigeStand(t.chat.wetterHolen)
      try {
        eingefuegt(await wetter.mutateAsync({ lat: ort.lat, lon: ort.lon, tz_offset_min: zone, draft: istEntwurf, client_key: schluessel, insertion: stelle }))
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.offline) {
          if (insertion !== null) {
            ohneNetzSagen(t.chat.einfuegen.brauchtNetz)
            return
          }
          await inDenKorb()
          return
        }
        throw fehler
      }
      zeigeStand(null)
    } catch (fehler) {
      zeigeFehler(fehler)
    }
  }

  /** The weather at a chosen place and hour (#83): the sheet chose, the server asks Apple. */
  async function wetterAnderswoSchicken(wahl: { lat: number; lon: number; at: string; tz_offset_min: number }) {
    setWetterwahlOffen(false)
    if (!netzStand()) {
      ohneNetzSagen(t.chat.wetterOhneNetz)
      return
    }
    zeigeStand(t.chat.wetterHolen)
    try {
      eingefuegt(await wetter.mutateAsync({ ...wahl, draft: istEntwurf, client_key: wetterSchluessel.keyFor(JSON.stringify([wahl, stelle])), insertion: stelle }))
      wetterSchluessel.forget()
      zeigeStand(null)
    } catch (fehler) {
      zeigeFehler(fehler)
    }
  }

  /**
   * The day card: ask the server, draw the map, send -- three steps, each
   * says what it does. Without the net not at all.
   */
  async function tagFragen() {
    if (!netzStand()) {
      ohneNetzSagen(t.chat.tagOhneNetz)
      return
    }
    const nummer = ++tagAnfrage.current
    zeigeStand(null)
    setTagLaedt(true)
    try {
      // The days the route has, and the proposal -- the question comes
      // before any drawing, so that nothing is drawn for the wrong day.
      // "Nur Fahrt" is on for a new card (1.151.0), so the day is read so.
      const vorschau = await api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, { moving_only: '1' })
      if (nummer !== tagAnfrage.current) return
      setTagwahl(vorschau)
    } catch (fehler) {
      if (nummer === tagAnfrage.current) zeigeFehler(fehler)
    } finally {
      if (nummer === tagAnfrage.current) setTagLaedt(false)
    }
  }

  /** "Abbrechen" on the waiting sheet: the answer, when it comes, is dropped. */
  function tagWartenAbbrechen() {
    tagAnfrage.current++
    setTagLaedt(false)
    if (tagAenderung !== null) onTagAenderungFertig?.()
  }

  // The sheet for a card to change opens with that card's day (1.86.0).
  useEffect(() => {
    if (tagAenderung === null || (tagAenderung.day_card ?? null) === null) return
    if (!netzStand()) {
      ohneNetzSagen(t.chat.tagOhneNetz)
      onTagAenderungFertig?.()
      return
    }
    const nummer = ++tagAnfrage.current
    const gilt = () => nummer === tagAnfrage.current
    setTagLaedt(true)
    const karte = tagAenderung.day_card!
    // The card's own days and counting (1.151.0); a card from before has neither and counted every point.
    // And the recordings it left out (1.176.0).
    api.get<DayCardPreview>(`/api/trips/${tripId}/daycard`, { day: karte.day, day_to: karte.day_to ?? undefined, moving_only: karte.moving_only === true ? '1' : undefined, skip_tracks: skipQuery(karte.skipped_tracks) })
      .then((vorschau) => { if (gilt()) setTagwahl(vorschau) })
      .catch((fehler) => { if (gilt()) { zeigeFehler(fehler); onTagAenderungFertig?.() } })
      .finally(() => { if (gilt()) setTagLaedt(false) })
    return () => {
      if (gilt()) {
        tagAnfrage.current++
        setTagLaedt(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagAenderung?.id])

  /*
   * Leaving the app cancels the film (decided: "bei Abbruch des Benutzers /
   * Verlassen der App: nichts am Server, muss dann neu gestartet werden").
   * A telephone stops the work of a page in the background anyway; a film
   * that went on afterwards would come back half-drawn.
   */
  const filmRunning = film !== null
  useEffect(() => {
    if (!filmRunning) return
    const gone = () => {
      if (document.visibilityState !== 'hidden' || filmSignal.current === null) return
      cancelReason.current = 'left'
      filmSignal.current.cancelled = true
    }
    document.addEventListener('visibilitychange', gone)
    window.addEventListener('pagehide', gone)
    return () => {
      document.removeEventListener('visibilitychange', gone)
      window.removeEventListener('pagehide', gone)
    }
  }, [filmRunning])

  function cancelFilm() {
    if (filmSignal.current === null) return
    cancelReason.current = 'button'
    filmSignal.current.cancelled = true
    // While the day is read nothing runs that could notice the flag soon:
    // the sheet goes at once, the message follows when the answer is there.
    setFilm((old) => (old?.step === 'prepare' ? null : old))
  }

  async function tagSchicken(wahl: { day: string; from?: string; to?: string; dayTo?: string; movingOnly: boolean; skip?: number[]; showRows: boolean; film?: FilmSettings | null; tour?: TourChoice | null; stored?: DayCardFilm; display: DayCardDisplay; rebuild?: boolean }) {
    setTagwahl(null)
    // The day card's own stroke (#80; until then the map's, #50): the map
    // of the day is a picture in the chat, and a line that reads well on
    // the live map can be too thin there. The pins carry the colour of a
    // published point, as on the map.
    const strich = {
      farbe: reiseKarte?.daycard_route_color ?? STRICH_VORGABE.farbe,
      breite: reiseKarte?.daycard_route_width ?? STRICH_VORGABE.breite,
      nadel: reiseKarte?.pin_color ?? '#007aff',
    }
    // The same day keeps its key until the card is really in the chat
    // (1.75.0): the sheet closes on the tap, so a retry comes through a
    // new sheet -- the key lives here, where the thread lives.
    const clientKey = tagSchluessel.keyFor(JSON.stringify([wahl.day, wahl.from ?? null, wahl.to ?? null, wahl.dayTo ?? null, wahl.movingOnly, wahl.skip ?? null, wahl.showRows, istEntwurf, stelle, wahl.display]))
    const zuAendern = tagAenderung?.id
    const filmSettings = wahl.film ?? null
    // The four switches (#139, one way since 1.125.0): all off is the film as it always was.
    const tour = wahl.tour ?? { ...TOUR_DEFAULTS, source: null }
    const signal: FilmSignal = { cancelled: false }
    const tap = performance.now()
    // The relief's map begins now, beside the server reading the day
    // (1.130.0): its style needs the build, not the day. Closed in
    // `finally`, whatever happens -- a cancel leaves no map behind.
    const terrainStart = tour.terrain && filmSettings !== null && showsPicture(wahl.display) ? beginTerrain(tap) : null
    // The sixth switch, where this device can draw it (1.137.0).
    const solid = tour.vehicle3d && canDraw3d()
    // The film's sheet stands from the tap on (1.125.0), not only once the
    // server has read the day: that wait was more than a second with nothing
    // on screen. Without a film the line under the chat says it, as before.
    if (filmSettings !== null && showsPicture(wahl.display)) {
      filmSignal.current = signal
      cancelReason.current = 'button'
      setFilm({ step: 'prepare', share: 0 })
    } else {
      zeigeStand(t.chat.tagAuswerten)
    }
    /** The uploaded video, until the card holds it: a cancel discards it. */
    let videoId: number | null = null
    let sent = false
    /** A video was asked for and a picture goes out: said afterwards, not silent. */
    let pictureInstead: string | null = null
    // The video the card holds, kept when nothing it was made from changed
    // (1.132.0) -- unless "Video neu rechnen" is ticked.
    const hasVideo = tagAenderung?.media.some((m) => m.kind === 'video') === true
    const keepIf = !wahl.rebuild && hasVideo ? tagAenderung?.day_card?.film?.input ?? null : null
    try {
      const antwort = await tageskarte.mutateAsync({
        day: wahl.day,
        from: wahl.from,
        to: wahl.to,
        dayTo: wahl.dayTo,
        movingOnly: wahl.movingOnly,
        skip: wahl.skip,
        showRows: wahl.showRows,
        draft: istEntwurf,
        clientKey,
        // A new card goes where the marker says (#130); a card computed anew stays where it is.
        insertion: zuAendern === undefined ? stelle : null,
        messageId: zuAendern,
        display: wahl.display,
        zeichnen: (punkte, nadeln) => zeichneTagesroute(punkte, buchquelle, strich, nadeln),
        film: filmSettings === null ? undefined : async (preview) => {
          // The size and rate of an uploaded video (1.107.0), or the base
          // film where this device cannot encode them.
          // Cancelled while the day was read: nothing more to do.
          const dayMs = Math.round(performance.now() - tap)
          if (signal.cancelled) throw new Error(t.chat.film.cancelled)
          const encodable = await encodableFormat(filmFormat(await ensureSettings(queryClient)), testCodecs())
          if (encodable === null) {
            setFilm(null)
            pictureInstead = t.chat.dayFilmUnsupported
            return null
          }
          const { format, choice } = encodable
          // The film's map (D): its own choice, or the trip's.
          const source = tour.source ?? buchquelle
          // With the profile, the views are fitted above its strip (1.148.0).
          let script = scriptFor(preview, source, filmSettings, format, tour.profile ? profileCover(format) : 0)
          if (script === null) {
            notiere('Routenfilm: kein Abschnitt mit Strecke, die Karte geht als Bild')
            setFilm(null)
            pictureInstead = t.chat.dayFilmNoSection
            return null
          }
          if (signal.cancelled) throw new Error(t.chat.film.cancelled)
          // Known before the first frame (1.141.0): said in the sheet while
          // the film is made, and gone with it -- not left in the line under
          // the chat, where it stood after the card was long sent.
          const profile = tour.profile ? profileOf(script, preview.sections ?? []) : null
          const note = tour.profile && profile === null ? t.chat.tour.noHeights : undefined
          // No heights, no strip: the map takes the whole picture again.
          if (tour.profile && profile === null) script = scriptFor(preview, source, filmSettings, format) ?? script
          setFilm({ step: 'map', share: 0, note })
          let made
          try {
            const progress = {
              choice,
              onStage: (step: 'map' | 'compute') => setFilm({ step, share: 0, note }),
              onShare: (share: number) => setFilm((old) => (old === null ? null : { ...old, share })),
              signal,
              codecs: testCodecs(),
              format,
            }
            // One way (1.125.0): the camera of the four switches over the
            // script; with all of them off it is the script's own.
            const maxZoom = Math.min(source.max_zoom, FILM_MAX_ZOOM)
            made = await buildRouteFilm(tourScript(script, format, { follow: tour.follow, tilt: tour.tilt }, maxZoom, format.fps), profile, source, strich, {
              ...progress,
              heightLabel: (metres) => t.chat.tour.heightLabel(Math.round(metres).toLocaleString(t.allgemein.gebiet)),
              northLetter: t.chat.tour.compassNorth,
              terrain: terrainStart,
              vehicle3d: solid,
              dayMs,
              server: preview.timings ?? null,
            })
            // Asked for and fallen back (#140): the film is there, without relief, and the line says so.
            if (tour.terrain && !made.terrain) pictureInstead = t.chat.tour.noTerrain
            // The same for the 3D vehicle (1.137.0); the relief's sentence comes first.
            else if (solid && !made.vehicle3d) pictureInstead = t.chat.tour.noVehicle3d
          } catch (error) {
            // A device without an encoder for this size still gets its card:
            // the map as a picture, as before #90. Everything else stops.
            if (signal.cancelled || !(error instanceof Error) || error.message !== t.technik.routeFilm.noEncoder) throw error
            setFilm(null)
            pictureInstead = t.chat.dayFilmUnsupported
            return null
          }
          setFilm({ step: 'upload', share: 0, note })
          const file = new File([await stampRouteFilm(made.video, preview.start, preview.stats.started_at)], `route-${preview.day}.mp4`, { type: 'video/mp4' })
          const media = await transferVideo(
            tripId,
            file,
            { width: made.width, height: made.height, durationMs: made.durationMs },
            Promise.resolve(made.poster),
            (share) => setFilm({ step: 'upload', share, note }),
            // `transferVideo` asks the existing flag `abgebrochen`.
            {
              signal: { get abgebrochen() { return signal.cancelled } },
              onRegistered: (id) => { videoId = id },
              // The bar stands while the net is gone (1.176.1); the sheet says why.
              onNetWait: (waiting) => setFilm((old) => (old === null ? old : { ...old, waitsForNet: waiting })),
            },
          )
          videoId = media.id
          // The last moment to say no: after this the card goes out.
          if (signal.cancelled) throw new Error(t.chat.film.cancelled)
          return media.id
        },
        elevation: filmSettings !== null && tour.profile,
        stored: wahl.stored,
        fingerprint: filmSettings === null ? undefined : async (preview) => {
          const encodable = await encodableFormat(filmFormat(await ensureSettings(queryClient)), testCodecs())
          if (encodable === null) return null
          // `vehicle3d` only when it draws (1.137.0): a video of a card from
          // before keeps its fingerprint, and with it its video.
          const { vehicle3d: _vehicle3d, ...flat } = tour
          return filmInput({ preview, settings: filmSettings, tour: solid ? { ...flat, source: tour.source ?? buchquelle, vehicle3d: true } : { ...flat, source: tour.source ?? buchquelle }, display: wahl.display, stroke: strich, format: encodable.format })
        },
        keepIf,
        kept: (preview) => {
          notiere(`Routenfilm: Eingaben unverändert, Video behalten (${dayLine(Math.round(performance.now() - tap), preview.timings ?? null)})`)
          setFilm(null)
          pictureInstead = t.chat.film.kept
        },
        vorher: (stand) => zeigeStand(stand === 'zeichnen' ? t.chat.tagZeichnen : t.chat.tagSchicken),
      })
      if (zuAendern === undefined) eingefuegt(antwort)
      sent = true
      tagSchluessel.forget()
      zeigeErgebnis(pictureInstead)
    } catch (fehler) {
      if (signal.cancelled) {
        zeigeErgebnis(cancelReason.current === 'left' ? t.chat.film.left : t.chat.film.cancelled)
      } else {
        zeigeFehler(fehler)
      }
    } finally {
      // Nothing on the server without a card: an uploaded film that no card
      // holds goes again. A refusal (it does hang on the card after all, the
      // answer was lost) leaves it where it is.
      if (!sent && videoId !== null) {
        const discarded = videoId
        void api.delete(`/api/media/${discarded}`).then(
          () => notiere(`Routenfilm: Video ${discarded} verworfen`),
          () => undefined,
        )
      }
      void terrainStart?.then((start) => start.close(), () => undefined)
      filmSignal.current = null
      setFilm(null)
      if (zuAendern !== undefined) onTagAenderungFertig?.()
    }
  }

  /**
   * Musik in den Chat (#102): der Link geht an den Server, der prüft ihn.
   *
   * **Ohne Netz gar nicht**, und das ist der Unterschied zum Wetter: ein
   * Standort ist auch offline eine Tatsache, ein Link erst nach der Prüfung
   * einer. Wirft weiter, damit das Blatt stehen bleibt und die Absage unter
   * dem Feld steht, in dem der Link noch drin ist.
   */
  async function musikSchicken(eingabe: { url: string; body: string; client_key: string }) {
    if (!netzStand()) {
      throw new Error(t.chat.musikOhneNetz)
    }
    zeigeStand(t.chat.musikSchicken)
    try {
      eingefuegt(await musik.mutateAsync({ ...eingabe, draft: istEntwurf, insertion: stelle }))
      zeigeStand(null)
    } catch (fehler) {
      zeigeStand(null)
      throw fehler
    }
  }

  /**
   * "Link zur Karte" (1.113.0): the chosen points go to the server, which
   * writes the post with their cards. Only online, like the music: which
   * points are published is the server's answer. Throws on, so the sheet
   * stays with its choice and the refusal stands under it.
   */
  async function kartenlinkSchicken(eingabe: { ids: number[]; client_key: string }) {
    if (!netzStand()) {
      throw new Error(t.chat.kartenlink.ohneNetz)
    }
    zeigeStand(t.chat.kartenlink.sendet)
    try {
      eingefuegt(await kartenlink.mutateAsync({ ...eingabe, draft: istEntwurf, insertion: stelle }))
      zeigeStand(null)
    } catch (fehler) {
      zeigeStand(null)
      throw fehler
    }
  }

  return {
    anhangOffen,
    setAnhangOffen,
    gifOffen,
    setGifOffen,
    abstimmungOffen,
    setAbstimmungOffen,
    wetterwahlOffen,
    setWetterwahlOffen,
    tagwahl,
    setTagwahl,
    /** The sheet before its days are known, and its way out (1.100.0). */
    tagLaedt,
    tagWartenAbbrechen,
    /** The film's sheet (#90): its state, and the way out. */
    film,
    cancelFilm,
    musikOffen,
    setMusikOffen,
    /** Welcher Schritt gerade läuft -- geht an die eine Meldezeile. */
    aktionStand,
    nachNachricht,
    ohneNetzSagen,
    wetterSchicken,
    wetterAnderswoSchicken,
    tagFragen,
    tagSchicken,
    musikSchicken,
    kartenlinkOffen,
    setKartenlinkOffen,
    kartenlinkSchicken,
  }
}

/**
 * The codecs the film may use -- **a door for the Prüfstand**, like
 * `kodierungen` at `verkleinereVideo()`: the Chromium there has no H.264.
 * In use it is never set, and `ZIELKODIERUNGEN` applies.
 */
function testCodecs(): readonly string[] | undefined {
  const door = (globalThis as { __tbFilmCodecs?: unknown }).__tbFilmCodecs
  return Array.isArray(door) && door.every((k) => typeof k === 'string') ? (door as string[]) : undefined
}
